import { Link } from 'react-router-dom';
import { products } from '../../../mocks/products';

interface ProductDetailUseCasesProps {
  currentProductId: string;
}

const useCaseBlocks = [
  {
    icon: 'ri-water-flash-line',
    label: 'Reef Cracks',
    description: 'Fast structural repair for reef impacts. Back in the water same session.',
    image: 'https://public.readdy.ai/ai/img_res/315904c2926c8c5ea8255e165ddb02fd.jpg',
  },
  {
    icon: 'ri-road-map-line',
    label: 'Rail Damage',
    description: 'Restore rail strength and waterproofing with a clean sun-cured repair.',
    image: 'https://readdy.ai/api/search-image?query=surfboard%20rail%20damage%20crack%20being%20repaired%20close%20up%20repair%20kit%20resin%20application%20beach%20sunny&width=500&height=350&seq=uc_rail1&orientation=landscape',
  },
  {
    icon: 'ri-suitcase-2-line',
    label: 'Travel Damage',
    description: 'Fix airline or transit dings at the destination before you hit the water.',
    image: 'https://readdy.ai/api/search-image?query=surfer%20inspecting%20surfboard%20travel%20damage%20hotel%20repair%20kit%20fixing%20board%20before%20surf%20trip%20tropical&width=500&height=350&seq=uc_travel1&orientation=landscape',
  },
  {
    icon: 'ri-home-4-line',
    label: 'Home Workshop',
    description: 'DIY repairs between sessions. No shop visit. No expensive ding repair bills.',
    image: 'https://readdy.ai/api/search-image?query=surfboard%20DIY%20repair%20at%20home%20garage%20workshop%20repair%20tools%20fiberglass%20resin%20application%20sunlight%20window&width=500&height=350&seq=uc_home1&orientation=landscape',
  },
];

export default function ProductDetailUseCases({ currentProductId }: ProductDetailUseCasesProps) {
  const related = products.filter((p) => p.id !== currentProductId).slice(0, 3);

  return (
    <section className="py-24 bg-[#F5EFE6]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#FF6B35] text-sm font-bold uppercase tracking-widest mb-3">Use Cases</p>
          <h2 className="text-4xl font-black text-stone-900">
            Fix Any Damage,<br />
            <span className="text-[#0B5C4D]">Anywhere</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {useCaseBlocks.map((uc) => (
            <div key={uc.label} className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-default">
              <div className="h-44 overflow-hidden">
                <img src={uc.image} alt={uc.label} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
                    <i className={`${uc.icon} text-[#FF6B35] text-sm`} />
                  </div>
                  <span className="text-stone-900 font-bold text-sm">{uc.label}</span>
                </div>
                <p className="text-stone-500 text-xs leading-relaxed">{uc.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Related products */}
        <h3 className="text-2xl font-black text-stone-900 mb-6">You Might Also Need</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {related.map((p) => (
            <Link key={p.id} to={`/products/${p.slug}`} className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform cursor-pointer">
              <div className="h-44 overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 flex items-center justify-between">
                <div>
                  <h4 className="text-stone-900 font-bold text-sm mb-0.5 group-hover:text-[#0B5C4D] transition-colors">{p.name}</h4>
                  <p className="text-[#FF6B35] font-bold text-sm">${p.price}</p>
                </div>
                <i className="ri-arrow-right-line text-stone-300 group-hover:text-[#0B5C4D] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
